import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Box, Dialog, Typography, alpha } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import { aplanarMenu, getMenu, RUTA_BUSQUEDA_NORMATIVAS } from './menuConfig';
import { useModulosUsuario } from '../../hooks/useModulosUsuario';
import { useColorMode } from '../../styles/ColorModeProvider';
import { cerrarSesion } from '../http/HttpCliente';

const Item = ({ icon: Icon, label, detalle, onSelect, value, keywords, forceMount }) => (
  <Command.Item value={value || label} keywords={keywords} onSelect={onSelect} forceMount={forceMount}>
    <Box className="cp-icono"><Icon fontSize="small" /></Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
        {label}
      </Typography>
      {detalle && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {detalle}
        </Typography>
      )}
    </Box>
  </Command.Item>
);

/**
 * Buscador global (Ctrl+K): ir a cualquier sección permitida por el rol,
 * buscar normativas por nombre/código y ejecutar acciones rápidas.
 */
const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [busqueda, setBusqueda] = useState('');
  const { rol, cargando, tieneServicio } = useModulosUsuario();
  const { mode, toggleColorMode } = useColorMode();

  const secciones = useMemo(
    () => aplanarMenu(getMenu(rol, (s) => !cargando && tieneServicio(s))),
    [rol, cargando, tieneServicio]
  );
  const rutaBusqueda = RUTA_BUSQUEDA_NORMATIVAS[rol];
  const texto = busqueda.trim();

  const ejecutar = (accion) => {
    onClose();
    setBusqueda('');
    accion();
  };
  const ir = (ruta) => ejecutar(() => navigate(ruta));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onExited: () => setBusqueda('') }}
      PaperProps={{
        sx: { position: 'fixed', top: { xs: 16, sm: '12vh' }, m: { xs: 1.5, sm: 2 }, overflow: 'hidden' },
      }}
    >
      <Box
        component={Command}
        label="Buscador global"
        loop
        sx={(t) => ({
          '& [cmdk-input]': {
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            font: 'inherit',
            fontSize: '1rem',
            color: 'text.primary',
            py: 2,
            '&::placeholder': { color: t.palette.text.disabled },
          },
          '& [cmdk-list]': {
            maxHeight: 'min(420px, 60vh)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            p: 1,
          },
          '& [cmdk-group-heading]': {
            px: 1.5, pt: 1.25, pb: 0.5,
            fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'text.secondary',
          },
          '& [cmdk-item]': {
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
            userSelect: 'none', transition: 'background-color .1s',
          },
          '& [cmdk-item][data-selected="true"]': {
            bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.09 : 0.2),
          },
          '& [cmdk-item][data-selected="true"] .cp-icono': {
            bgcolor: 'primary.main', color: 'primary.contrastText', borderColor: 'primary.main',
          },
          '& .cp-icono': {
            width: 32, height: 32, flexShrink: 0, borderRadius: 1.5,
            display: 'grid', placeItems: 'center',
            color: 'text.secondary', border: `1px solid ${t.palette.divider}`,
            transition: 'background-color .1s, color .1s',
          },
          '& [cmdk-empty]': { py: 5, textAlign: 'center', color: 'text.secondary', fontSize: '0.9rem' },
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <SearchIcon sx={{ color: 'text.secondary' }} />
          <Command.Input
            value={busqueda}
            onValueChange={setBusqueda}
            placeholder={rutaBusqueda ? 'Buscar secciones, acciones o normativas…' : 'Buscar secciones o acciones…'}
            autoFocus
          />
          <kbd>Esc</kbd>
        </Box>

        <Command.List>
          <Command.Empty>No encontramos coincidencias para “{texto}”.</Command.Empty>

          {rutaBusqueda && texto && (
            <Command.Group heading="Normativas" forceMount>
              <Item
                forceMount
                value={`buscar-normativas-${texto}`}
                icon={ManageSearchIcon}
                label={`Buscar “${texto}” en normativas`}
                detalle="Busca por nombre o código en el listado de normativas"
                onSelect={() => ir(`${rutaBusqueda}?q=${encodeURIComponent(texto)}`)}
              />
            </Command.Group>
          )}

          <Command.Group heading="Ir a">
            {secciones.map((s) => (
              <Item
                key={s.ruta}
                value={s.ruta}
                keywords={[s.label, ...s.grupos.map((g) => g.label)]}
                icon={s.icon}
                label={s.label}
                detalle={s.grupos.map((g) => g.label).join(' › ') || undefined}
                onSelect={() => ir(s.ruta)}
              />
            ))}
          </Command.Group>

          <Command.Group heading="Acciones">
            {rol === 'Administrador' && (
              <Item
                value="accion-nueva-normativa"
                keywords={['nueva normativa', 'agregar', 'crear']}
                icon={AddCircleOutlineIcon}
                label="Crear nueva normativa"
                onSelect={() => ir('/nueva-normativa')}
              />
            )}
            <Item
              value="accion-tema"
              keywords={['tema', 'oscuro', 'claro', 'modo']}
              icon={mode === 'dark' ? LightModeOutlinedIcon : DarkModeOutlinedIcon}
              label={mode === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
              onSelect={() => ejecutar(toggleColorMode)}
            />
            {rol !== 'Suscriptor' && (
              <Item
                value="accion-perfil"
                keywords={['perfil', 'cuenta', 'usuario', 'contraseña']}
                icon={PersonOutlineIcon}
                label="Perfil de usuario"
                onSelect={() => ir('/perfil-usuario')}
              />
            )}
            <Item
              value="accion-salir"
              keywords={['cerrar sesión', 'salir', 'logout']}
              icon={LogoutIcon}
              label="Cerrar sesión"
              onSelect={() => ejecutar(async () => { await cerrarSesion(); navigate('/login'); })}
            />
          </Command.Group>
        </Command.List>

        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' }, gap: 2, px: 2, py: 1.25,
            borderTop: 1, borderColor: 'divider', color: 'text.secondary', fontSize: '0.75rem',
            '& span': { display: 'inline-flex', alignItems: 'center', gap: 0.75 },
          }}
        >
          <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
          <span><kbd>Enter</kbd> abrir</span>
          <span><kbd>Esc</kbd> cerrar</span>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
